import * as pdfjsLib from "pdfjs-dist";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";


dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const normalizePdfUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  const normalized = path.startsWith("/pdfs/") ? path : `/pdfs${path}`;
  return `${window.location.origin}${normalized}`;
};

export const normalizeDashes = (value) => String(value || "").replace(/[\u2010-\u2015\u2212]/g, "-");

const WEEKDAY_ORDER = ["Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const DAY_TOKEN_PATTERN = "(?:Mo|Di|Mi|Do|Fr|Sa|SF)";
const DAY_TOKEN_MATCH_PATTERN = `\\b${DAY_TOKEN_PATTERN}(?:\\.|\\b)`;

const normalizeDayToken = (token) => String(token || "").replace(/\./g, "").trim();

const expandRange = (start, end) => {
  const startIdx = WEEKDAY_ORDER.indexOf(start);
  const endIdx = WEEKDAY_ORDER.indexOf(end);
  if (startIdx < 0 || endIdx < 0 || endIdx < startIdx) return [start, end];
  return WEEKDAY_ORDER.slice(startIdx, endIdx + 1);
};

const canonicalizeDays = (days) => {
  const ordered = WEEKDAY_ORDER.filter((day) => days.includes(day));
  if (ordered.length === 0) return null;
  if (ordered.length === 1) return ordered[0];

  const isContiguous = ordered.every((day, index) => {
    if (index === 0) return true;
    const prev = WEEKDAY_ORDER.indexOf(ordered[index - 1]);
    const curr = WEEKDAY_ORDER.indexOf(day);
    return curr === prev + 1;
  });

  if (isContiguous) return `${ordered[0]}-${ordered[ordered.length - 1]}`;
  return ordered.join("+");
};

export const normalizeDaycode = (value) => {
  const normalized = normalizeDashes(String(value || ""));
  const sequenceMatch = normalized.match(
    new RegExp(`(?:${DAY_TOKEN_MATCH_PATTERN}\\s*(?:[+-]\\s*${DAY_TOKEN_MATCH_PATTERN}\\s*)+)`, "i")
  );

  let hasSF = /\bSF\.?\b/i.test(normalized);
  const daySet = new Set();

  if (sequenceMatch) {
    const sequence = sequenceMatch[0];
    const dayTokens = (sequence.match(new RegExp(DAY_TOKEN_MATCH_PATTERN, "gi")) || []).map(normalizeDayToken);
    const operators = sequence.match(/[+-]/g) || [];

    if (dayTokens.length > 0) {
      if (dayTokens[0] === "SF") {
        hasSF = true;
      } else {
        daySet.add(dayTokens[0]);
      }

      for (let i = 0; i < operators.length; i++) {
        const op = operators[i];
        const from = dayTokens[i];
        const to = dayTokens[i + 1];
        if (!to) continue;

        if (to === "SF" || from === "SF") {
          hasSF = true;
          continue;
        }

        if (op === "-") {
          for (const day of expandRange(from, to)) daySet.add(day);
        } else {
          daySet.add(to);
        }
      }
    }
  }

  if (daySet.size === 0) {
    const singleMatch = normalized.match(new RegExp(DAY_TOKEN_MATCH_PATTERN, "i"));
    if (singleMatch) {
      const day = normalizeDayToken(singleMatch[0]);
      if (day === "SF") hasSF = true;
      else daySet.add(day);
    }
  }

  const canonical = canonicalizeDays(Array.from(daySet));
  if (canonical) return canonical;
  return hasSF ? "SF" : null;
};

export const daycodeIncludesWeekday = (daycode, weekdayCode) => {
  const normalized = normalizeDaycode(daycode);
  if (!normalized) return false;
  if (normalized === "SF") return false;

  const days = normalized.includes("-")
    ? expandRange(...normalized.split("-"))
    : normalized.split("+");

  return days.includes(weekdayCode);
};

export const daycodeMatchesDate = (daycode, datePicked, forceSF = false) => {
  const normalized = normalizeDaycode(daycode);
  if (!normalized) return false;

  if (forceSF) return normalized === "SF";

  const dayOfWeek = dayjs(datePicked).day();
  if (dayOfWeek === 0) return normalized === "SF";

  const weekdayMap = {
    1: "Mo",
    2: "Di",
    3: "Mi",
    4: "Do",
    5: "Fr",
    6: "Sa"
  };

  const weekdayCode = weekdayMap[dayOfWeek];
  if (!weekdayCode) return false;
  return daycodeIncludesWeekday(normalized, weekdayCode);
};

export const extractPageText = async (page) => {
  const textContent = await page.getTextContent();
  if (!textContent || !Array.isArray(textContent.items)) return "";
  return textContent.items.map((item) => item.str || "").join("\n");
};

const parseSeiteCounter = (pageText) => {
  const match = pageText.match(/Seite\s+(\d+)\s+von\s+(\d+)/i);
  if (!match) return null;
  return {
    current: parseInt(match[1], 10),
    total: parseInt(match[2], 10)
  };
};

const matchesDienstNummer = (pageText, number) => {
  const normalized = normalizeDashes(pageText);
  const regex = /Dienst-Nr\.\s*:\s*326\s*(\d{4})/i;
  const match = normalized.match(regex);
  if (!match) return false;
  return match[1] === number;
};

// Helper to check if a date is a holiday (Feiertag) in Firestore
const isHoliday = async (datePicked) => {
  const isoDate = dayjs(datePicked).format("YYYY-MM-DD");
  const q = query(collection(db, "Feiertage"), where("date", "==", isoDate));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

const matchesDayBeforeUmlauf = (pageText, datePicked, forceSF = false) => {
  const normalized = normalizeDashes(pageText);
  const umlaufIndex = normalized.search(/Umlauf/i);
  const beforeUmlauf = umlaufIndex >= 0 ? normalized.slice(0, umlaufIndex) : normalized;
  const extractedDaycode = normalizeDaycode(beforeUmlauf);
  return daycodeMatchesDate(extractedDaycode, datePicked, forceSF);
};

async function searchNumberInPdf(pdfUrl, number, renderPageToImage, datePicked) {
  const loadingTask = pdfjsLib.getDocument(pdfUrl);
  const pdf = await loadingTask.promise;
  const pageCache = new Map();

  // Check if the picked date is a holiday
  const holiday = await isHoliday(datePicked);

  const readPageText = async (pageNum) => {
    if (pageCache.has(pageNum)) return pageCache.get(pageNum);
    const page = await pdf.getPage(pageNum);
    const pageText = await extractPageText(page);
    const entry = { page, pageText };
    pageCache.set(pageNum, entry);
    return entry;
  };

  let selectedPageNum = null;
  let selectedCounter = null;

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const { pageText } = await readPageText(pageNum);
    if (!matchesDienstNummer(pageText, number)) continue;
    if (!matchesDayBeforeUmlauf(pageText, datePicked, holiday)) continue;

    selectedPageNum = pageNum;
    selectedCounter = parseSeiteCounter(pageText);
    break;
  }

  if (!selectedPageNum) return [];

  const pageNums = new Set([selectedPageNum]);

  if (selectedCounter && selectedCounter.total && selectedCounter.current) {
    let prevExpected = selectedCounter.current - 1;
    for (let pageNum = selectedPageNum - 1; pageNum >= 1; pageNum--) {
      const { pageText } = await readPageText(pageNum);
      const counter = parseSeiteCounter(pageText);
      if (!counter || counter.total !== selectedCounter.total) break;
      if (counter.current !== prevExpected) break;
      pageNums.add(pageNum);
      prevExpected -= 1;
      if (counter.current === 1) break;
    }

    let nextExpected = selectedCounter.current + 1;
    for (let pageNum = selectedPageNum + 1; pageNum <= pdf.numPages; pageNum++) {
      const { pageText } = await readPageText(pageNum);
      const counter = parseSeiteCounter(pageText);
      if (!counter || counter.total !== selectedCounter.total) break;
      if (counter.current !== nextExpected) break;
      pageNums.add(pageNum);
      nextExpected += 1;
      if (counter.current === selectedCounter.total) break;
    }
  }

  const sortedPageNums = Array.from(pageNums).sort((a, b) => a - b);
  const finalMatches = [];

  for (const pageNum of sortedPageNums) {
    const imageUrl = await renderPageToImage(pdf, pageNum);
    finalMatches.push({ pageNum, imageUrl, crewSwap: [] });
  }

  return finalMatches;
}

async function loadPlansFromFirestore() {
  const snapshot = await getDocs(collection(db, "Fahrplaene"));
  const entries = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const pdfSnap = await getDocs(collection(db, "Fahrplaene", docSnap.id, "pdfs"));
    const pdfs = pdfSnap.docs
      .map((pdfDoc, index) => ({
        id: pdfDoc.id,
        ...pdfDoc.data(),
        order: pdfDoc.data().order ?? index + 1,
        pdfUrl: normalizePdfUrl(pdfDoc.data().pdfUrl)
      }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    entries.push({
      id: docSnap.id,
      dateStart: data.dateStart || "",
      dateEnd: data.dateEnd || "",
      pdfs
    });
  }

  return entries;
}

export async function handleSearch({
  number,
  setFoundPages,
  setLoading,
  renderPageToImage,
  datePicked,
  setMultipleBerechnung
}) {
  const dienstPrefix = String(number).replace(/\D/g, "").slice(0, 2);
  const datePickedJS = dayjs(datePicked).startOf("day");

  setLoading(true);
  setFoundPages([]);
  if (setMultipleBerechnung) setMultipleBerechnung(false);

  try {
    const entries = await loadPlansFromFirestore();
    const matchingPlan = entries.find((plan) => {
      if (!plan.dateStart || !plan.dateEnd) return false;
      const start = dayjs(plan.dateStart, "YYYY-MM-DD").startOf("day");
      const end = dayjs(plan.dateEnd, "YYYY-MM-DD").startOf("day");
      return datePickedJS.isSameOrAfter(start) && datePickedJS.isSameOrBefore(end);
    });

    if (!matchingPlan || !matchingPlan.pdfs || matchingPlan.pdfs.length === 0) {
      setFoundPages([]);
      return;
    }

    const matchingPdfs = matchingPlan.pdfs.filter((pdf) => {
      const dienstLinie = Array.isArray(pdf.dienstLinie) ? pdf.dienstLinie : [pdf.dienstLinie];
      const normalized = dienstLinie
        .map((item) => String(item || "").replace(/\D/g, "").slice(0, 2))
        .filter((item) => /^\d{2}$/.test(item));
      return normalized.includes(dienstPrefix);
    });

    const finalMatches = [];
    for (const pdf of matchingPdfs) {
      if (!pdf.pdfUrl) continue;
      const matches = await searchNumberInPdf(pdf.pdfUrl, number, renderPageToImage, datePicked);
      if (matches.length > 0) {
        finalMatches.push(...matches);
        break;
      }
    }

    setFoundPages(finalMatches);
  } catch (error) {
    console.error("Failed to load Fahrplaene:", error);
    setFoundPages([]);
  } finally {
    setLoading(false);
  }
}
